/**
 * Account utilities for Dogs Trust UK My Account
 *
 * This file contains utility functions for managing user accounts, including authentication,
 * profile management, session handling, and synchronization between online and offline profiles.
 * It provides functionality for user login/logout, tracking authentication events,
 * and managing account-related data in localStorage.
 */

import { syncProfile } from "../../../../utils/api/my-account";
import { getCookie } from "../../../../utils/cookies";
import { pushToDataLayer } from "../../../../utils/google";
import { generateUUID } from "../../../../utils/uuid";
import { DEFAULT_PROFILE } from "./consts";

import {
	confirmOfflineProfileHasMerged,
	getOfflineProfile,
	updateOfflineProfile,
} from "./Offline.utils";

/**
 * Checks the authentication status of the current user
 *
 * This function makes a request to the authentication status endpoint to determine
 * if the user is currently logged in. It returns the authentication state which can
 * be either "authorized" or "unauthorized".
 *
 * @returns Promise resolving to the user's authentication state
 */
export async function checkAuth(): Promise<DogsTrust.MyAccountAuthState> {
	try {
		const isLoggedIn = await fetch(process.env.GATSBY_AUTH_STATUS_URL, {
			credentials: "include",
			method: "GET",
			redirect: "manual",
		});
		const authStatus = isLoggedIn.ok ? "authorized" : "unauthorized";
		return authStatus;
	} catch (error) {
		console.error("🔍 checkAuth: Error checking authentication:", error);
		return "unauthorized";
	}
}

/**
 * Tracks authentication-related events by pushing them to the data layer
 *
 * This function checks for sign-in and sign-up cookies to determine if
 * authentication events need to be tracked. It pushes events to the data layer
 * for analytics tracking, ensuring each event is only tracked once.
 *
 * @param userId - The unique identifier for the user
 */
export function trackAuthEvents(userId: string) {
	const justSignedInCookie = getCookie("justsignedin");
	const justSignedUpCookie = getCookie("justsignedup");

	// Handle login event tracking
	if (justSignedInCookie && typeof window !== "undefined") {
		const alreadyAddedLoginToDataLayer = window.dataLayer?.some(
			(item: {
				event: string;
				authentication: string;
			}) =>
				item.event === "login" && item.authentication === justSignedInCookie,
		);

		if (!alreadyAddedLoginToDataLayer) {
			pushToDataLayer({
				event: "login",
				authentication: justSignedInCookie,
				user_id: userId,
			});
		}
	}

	// Handle signup event tracking
	if (justSignedUpCookie && typeof window !== "undefined") {
		const alreadyAddedSignupToDataLayer = window.dataLayer?.some(
			(item: {
				event: string;
				authentication: string;
			}) =>
				item.event === "account_creation" &&
				item.authentication === justSignedUpCookie,
		);

		if (!alreadyAddedSignupToDataLayer) {
			pushToDataLayer({
				event: "account_creation",
				authentication: justSignedUpCookie,
				user_id: userId,
			});
		}
	}
}

/**
 * Fetches the user's profile data from the server
 *
 * This function makes a request to the profile API to retrieve the user's account data.
 * It handles authentication tracking when a profile is successfully fetched and merges
 * the server profile with any offline data that may exist.
 *
 * @returns Promise resolving to the user's profile or null if retrieval fails
 */
export async function fetchAccountProfile(): Promise<DogsTrust.Profile> {
	try {
		// First check if we have a valid cached profile to avoid unnecessary fetching
		// This helps prevent race conditions during profile merging and synchronization
		const cachedProfile = getLocalAccountProfile();
		if (
			cachedProfile?.timestampForCacheValidation &&
			Date.now() - cachedProfile.timestampForCacheValidation < 10000
		) {
			// Within 10 seconds
			console.log(
				"🚀 fetchAccountProfile | using recent cached profile to avoid race conditions",
			);
			return cachedProfile;
		}

		const credentials =
			typeof window !== "undefined" &&
			window.location.href.includes("localhost")
				? "same-origin"
				: "include";

		const profileResponse = await fetch(process.env.GATSBY_PROFILE_API_URL, {
			method: "GET",
			redirect: "manual",
			headers: {
				"Content-Type": "application/json",
			},
			credentials,
		});

		// The user is logged in
		if (profileResponse.ok) {
			const profile: DogsTrust.Profile = await profileResponse.json();

			trackAuthEvents(profile.userId);

			console.log("🚀 fetchAccountProfile | profile", profile);
			const mergedProfile = handleMergeOfProfiles(profile);

			// Update local cache with the merged profile
			updateLocalAccountProfile(mergedProfile);

			return mergedProfile;
		}

		console.log("🚀 fetchAccountProfile | unableToFetchProfile");
		return null;
	} catch (error) {
		console.error("🔍 fetchProfile: Error fetching profile:", error);
		return null;
	}
}

/**
 * Removes the user's profile data from localStorage
 *
 * This function clears the locally stored account profile data from the browser,
 * typically used during logout or when refreshing profile data.
 */
export function clearLocalAccountProfile() {
	localStorage.removeItem("accountProfile");
}

/**
 * Retrieves the user's profile data from localStorage
 *
 * This function attempts to retrieve and parse the user's account profile from localStorage.
 * It also ensures that all basket items have unique IDs by generating UUIDs for any items
 * that are missing them.
 *
 * @returns The user's profile object or null if no profile exists or an error occurs
 */
export function getLocalAccountProfile(): DogsTrust.Profile | null {
	try {
		if (typeof localStorage === "undefined") {
			return null;
		}
		const profileString: string | null = localStorage.getItem("accountProfile");
		if (profileString) {
			const parsedProfile: DogsTrust.Profile = JSON.parse(profileString);

			// Ensure all basket items have IDs
			const updatedBasket = (parsedProfile.basket || []).map((dog) => ({
				...dog,
				basketId: dog.basketId || generateUUID(),
			}));

			return {
				...parsedProfile,
				basket: updatedBasket,
			};
		}
	} catch (error) {
		console.error("🚀 Error getting account profile:", error);
	}

	return null;
}

/**
 * Checks for a valid cached profile in localStorage
 *
 * This function retrieves the profile from localStorage and checks if it's valid:
 * 1. Must exist in localStorage
 * 2. Must have a timestamp
 * 3. Must be less than 30 minutes old
 *
 * @returns A valid profile that can be used or null if no valid cached profile exists
 */
export async function getValidCachedProfile(): Promise<DogsTrust.Profile | null> {
	try {
		const auth = await checkAuth();
		if (auth !== "authorized") {
			clearLocalAccountProfile();
			return null;
		}

		const profile = getLocalAccountProfile();

		// Check if profile exists and has a timestamp
		if (!profile || !profile.timestampForCacheValidation) {
			return null;
		}

		// Check if the profile is less than 30 minutes old (30 * 60 * 1000 = 1,800,000 milliseconds)
		const cacheAge = Date.now() - profile.timestampForCacheValidation;
		const THIRTY_MINUTES = 30 * 60 * 1000;

		if (cacheAge > THIRTY_MINUTES) {
			clearLocalAccountProfile();
			console.log("🚀 Cached profile is older than 30 minutes, cannot use");
			return null;
		}

		console.log("🚀 Using valid cached profile from localStorage");
		return profile;
	} catch (error) {
		console.error("🚀 Error checking cached profile validity:", error);
		clearLocalAccountProfile();
		return null;
	}
}

/**
 * Updates the user's profile data in localStorage
 *
 * This function saves the provided profile to localStorage, ensuring that
 * profile data persists between page reloads and browser sessions.
 * It also adds a timestamp to track when the profile was last updated.
 *
 * @param profile - The user profile to save in localStorage
 */
export function updateLocalAccountProfile(profile: DogsTrust.Profile) {
	// First check if there's existing data we need to preserve
	const existingProfile = getLocalAccountProfile();

	// Preserve any favorites, interested, and basket items that might be lost
	// in racing conditions during sync operations
	const updatedProfile = {
		...profile,
		timestampForCacheValidation: Date.now(), // Always update timestamp for cache validation
		favourites: profile.favourites || existingProfile?.favourites || [],
		interested: profile.interested || existingProfile?.interested || [],
		basket: profile.basket || existingProfile?.basket || [],
	};

	// Save the updated profile to localStorage
	localStorage.setItem("accountProfile", JSON.stringify(updatedProfile));
}

/**
 * Processes a dog enquiry and updates the user's profile
 *
 * When a user expresses interest in a dog, this function:
 * 1. Adds the dog to the user's favorites list (if not already present)
 * 2. Adds the dog to the user's interested list (if not already present)
 * 3. Handles analytics tracking for the action
 * 4. Clears the enquiry cookie
 *
 * @param dog - The dog data to process for enquiry
 * @param data - Tracking data for analytics
 * @param currentProfile - The current user profile
 * @returns Updated profile with the dog added to favorites and interested lists
 */
export function enquireAboutDog(
	dog: DogsTrust.DogData,
	data: DogsTrust.DogDataTracking,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	// Check if dog is already in favourites or interested lists
	const dogIsAlreadyInFavourites = currentProfile.favourites?.some(
		(favDog) => favDog?.apiKey === dog?.apiKey,
	);
	const dogIsAlreadyInInterested = currentProfile.interested?.some(
		(interestedDog) => interestedDog?.apiKey === dog?.apiKey,
	);

	// Handle tracking if dog is not already favorited
	if (!dogIsAlreadyInFavourites) {
		// We are only going to track adding to faves if the dog is not already faved
		const currentPageUrl =
			typeof window !== "undefined" ? window.location.pathname : "";
		data.card_location = currentPageUrl;
		data.card_title = dog.name;
		data.card_sub_title = dog.breed;
		data.card_state = "added";

		pushToDataLayer(data as Google.DataLayerObject);
	}

	// Clear the enquire cookie since we've processed it
	localStorage.removeItem(process.env.GATSBY_DOG_TO_ENQUIRE_COOKIE_NAME);

	// Return updated profile
	return {
		...currentProfile,
		favourites: dogIsAlreadyInFavourites
			? currentProfile.favourites
			: [...currentProfile.favourites, dog],
		interested: dogIsAlreadyInInterested
			? currentProfile.interested
			: [dog, ...(currentProfile.interested || [])].slice(0, 4),
	};
}

/**
 * Merges online and offline user profiles
 *
 * This function takes a user's account profile retrieved from the server and merges it with
 * any offline profile data stored locally. It handles deduplication of favorites, interested dogs,
 * and basket items, ensuring that data is not lost when transitioning between online and offline states.
 *
 * @param accountProfile - The user profile retrieved from the server
 * @returns Merged profile combining online and offline data
 */
export function handleMergeOfProfiles(
	accountProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	try {
		// Check if we have a recent cache with merged data
		const cachedProfile = getLocalAccountProfile();
		if (
			cachedProfile?.timestampForCacheValidation &&
			Date.now() - cachedProfile.timestampForCacheValidation < 30000 && // Within 30 seconds
			cachedProfile.userId === accountProfile.userId
		) {
			// If we have recent cached data, prefer it over the server data to prevent losing merged information
			console.log(
				"🚀 handleMergeOfProfiles | Using recent cached profile to preserve merged data",
			);
			return {
				...cachedProfile,
				timestampForCacheValidation: Date.now(), // Update timestamp
			};
		}

		const offlineProfile = getOfflineProfile();
		console.log("🚀 handleMergeOfProfiles | offlineProfile", offlineProfile);
		if (!offlineProfile || offlineProfile.hasMerged) {
			return accountProfile;
		}

		// Merge and deduplicate favourites
		const mergedFaves = [
			...offlineProfile.favourites,
			...accountProfile.favourites,
		];
		const deduplicatedFaves: DogsTrust.DogData[] = Object.values(
			mergedFaves.reduce(
				(acc, dog) => {
					acc[dog.apiKey] = dog;
					return acc;
				},
				{} as Record<string, DogsTrust.DogData>,
			),
		);

		// Merge and deduplicate interested dogs
		const mergedInterested = [
			...offlineProfile.interested,
			...accountProfile.interested,
		];
		const deduplicatedInterested: DogsTrust.DogData[] = Object.values(
			mergedInterested.reduce(
				(acc, dog) => {
					acc[dog.apiKey] = dog;
					return acc;
				},
				{} as Record<string, DogsTrust.DogData>,
			),
		);

		// Merge and deduplicate basket
		const mergedBasket = [...offlineProfile.basket, ...accountProfile.basket];
		const deduplicatedBasket: DogsTrust.SADCookie[] = Object.values(
			mergedBasket.reduce(
				(acc, dog) => {
					acc[dog.basketId || generateUUID()] = dog;
					return acc;
				},
				{} as Record<string, DogsTrust.SADCookie>,
			),
		);

		// Create merged profile
		const mergedProfile: DogsTrust.Profile = {
			...accountProfile,
			basket: deduplicatedBasket.slice(0, 4), // The basket is max 4
			favourites: deduplicatedFaves,
			interested: deduplicatedInterested,
			shouldSync: true,
			timestampForCacheValidation: Date.now(), // Add fresh timestamp
		};

		// Update the account profile with merged data
		updateLocalAccountProfile(mergedProfile);

		// Mark the offline profile as merged to prevent multiple merges
		confirmOfflineProfileHasMerged();

		console.log("🚀 handleMergeOfProfiles | mergedProfile", mergedProfile);

		return mergedProfile;
	} catch (error) {
		console.error("🚀 handleMergeOfProfiles | Error merging profiles:", error);
		return accountProfile;
	}
}

/**
 * Handles user logout process
 *
 * This function makes a request to the sign-out endpoint to terminate the user's session.
 * On successful logout, it clears the user's account profile from localStorage and
 * resets the offline profile to the default state.
 *
 * @returns Promise resolving to the default profile on successful logout
 * @throws Error if the logout request fails
 */
export async function handleLogout(): Promise<void> {
	pushToDataLayer({
		event: "button_navigation",
		button_clicked: "logout",
	});

	clearLocalAccountProfile();
	updateOfflineProfile(DEFAULT_PROFILE);
	window.location.href = process.env.GATSBY_PROFILE_SIGN_OUT_URL;
	return;
}

/**
 * Synchronizes the user's profile with the server
 *
 * This function sends the local profile data to the server for synchronization,
 * ensuring that user data is consistent between the client and server. It's typically
 * called after making local changes to the profile that need to be persisted on the server.
 *
 * @param profile - The user profile to synchronize with the server
 * @returns Promise resolving to the synchronized profile on success
 * @throws Error if the profile is undefined or synchronization fails
 */
export const handleProfileSync = async (
	profile?: DogsTrust.Profile,
): Promise<DogsTrust.Profile> => {
	if (!profile) {
		throw new Error(
			"handleProfileSync: Unable to sync as profile is undefined",
		);
	}
	console.log("🚀 handleProfileSync | profile before sync:", profile);
	console.log(
		"🚀 handleProfileSync | favourites count before sync:",
		profile.favourites?.length || 0,
	);

	// Before syncing, save the current profile's important fields that we want to preserve
	const existingFavorites = profile.favourites || [];
	const existingInterested = profile.interested || [];
	const existingBasket = profile.basket || [];

	// Sync the profile to the server
	const syncSucceeded = await syncProfile(profile);

	if (syncSucceeded) {
		// Create a profile that preserves our local state after sync
		// This is critical to avoid race conditions with server-side data
		const preservedProfile = {
			...profile,
			favourites: existingFavorites,
			interested: existingInterested,
			basket: existingBasket,
			shouldSync: false, // Mark as already synced
		};

		// Also update the cached profile to ensure consistent state
		updateLocalAccountProfile(preservedProfile);

		console.log("🚀 handleProfileSync | profile after sync:", preservedProfile);
		console.log(
			"🚀 handleProfileSync | favourites count after sync:",
			preservedProfile.favourites?.length || 0,
		);

		return preservedProfile;
	}
	throw new Error("handleProfileSync Profile sync failed");
};
